import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { Select } from 'antd'
import { omit } from 'lodash'
import moment, { Moment } from 'moment'

import { Label } from '@/components/molecules/label'
import { Button } from '@/components/atoms'
import { DateField } from '@/components/molecules/date-field'
import { InputField } from '@/components/molecules/input-field'
import { EmployeeType, OperationLogFiltersType } from '@/models/operation-log'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { FEATURES_LIST, TABLES_LIST, normalizeOperationLogFeatureValue } from '@/constants/operation-log'
import { normalizeHalfHourSelectTime } from '@/utils/operation-log-select-time'
import { HALF_HOUR_TIME_OPTIONS } from '@/utils/half-hour-time-options'
import { getHotelEmployeeList } from '@/apis/aipass'
import { errorHandler } from '@/libs/errors'

const DEFAULT_BEGIN_TIME = '00:00'
const DEFAULT_END_TIME = '23:59'

const getDefaultDateRange = () => {
  const end = moment().endOf('day')
  const begin = moment().subtract(7, 'days').startOf('day')
  return {
    beginDate: begin,
    endDate: end,
    beginTime: DEFAULT_BEGIN_TIME,
    endTime: DEFAULT_END_TIME,
  }
}

type OperationLogSearchProps = {
  onChange: (data: OperationLogFiltersType) => void
  initialFilters?: OperationLogFiltersType
}

type OperationLogFiltersInputType = Omit<OperationLogFiltersType, 'beginDate' | 'endDate' | 'staff'> & {
  beginDate?: Moment
  beginTime?: string
  endDate?: Moment
  endTime?: string
  staff?: number | string
  reservationId?: string
  featureSlug?: string
  table?: string
  afterClosingAdjustment?: boolean
}

export const OperationLogSearch: React.FC<OperationLogSearchProps> = ({ onChange, initialFilters }) => {
  const { t, i18n } = useTranslation()
  const { employeeStaff } = useContext<AccountContextValue>(AccountContext)
  const defaultRange = getDefaultDateRange()
  const defaultValues: Partial<OperationLogFiltersInputType> = initialFilters
    ? {
        ...initialFilters,
        beginDate: initialFilters.beginDate ? moment(initialFilters.beginDate) : defaultRange.beginDate,
        endDate: initialFilters.endDate ? moment(initialFilters.endDate) : defaultRange.endDate,
        beginTime: initialFilters.beginDate?.includes(' ')
          ? (normalizeHalfHourSelectTime(initialFilters.beginDate.split(' ')[1]) ?? DEFAULT_BEGIN_TIME)
          : defaultRange.beginTime,
        endTime: initialFilters.endDate?.includes(' ')
          ? (normalizeHalfHourSelectTime(initialFilters.endDate.split(' ')[1]) ?? DEFAULT_END_TIME)
          : defaultRange.endTime,
        featureSlug: normalizeOperationLogFeatureValue(initialFilters.featureSlug),
        afterClosingAdjustment: Boolean(initialFilters.afterClosingAdjustment),
      }
    : {
        ...defaultRange,
        staff: undefined,
        employee: undefined,
        reservationId: undefined,
        featureSlug: undefined,
        table: undefined,
        afterClosingAdjustment: false,
      }

  const { control, handleSubmit, reset } = useForm<OperationLogFiltersInputType>({
    defaultValues,
  })
  const [employeeList, setEmployeeList] = useState<EmployeeType[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const [times] = useState([{ name: '指定なし', value: '' }, ...HALF_HOUR_TIME_OPTIONS.map(time => ({ value: time, name: time }))])

  const onClear = () => {
    setValidationError(null)
    reset({
      ...getDefaultDateRange(),
      staff: undefined,
      employee: undefined,
      reservationId: undefined,
      featureSlug: undefined,
      table: undefined,
      afterClosingAdjustment: false,
    })
  }

  const buildDateTime = (date: Moment | undefined, time: string | undefined, endDefault: boolean): string => {
    if (!date) return ''
    const dateStr = date.format('YYYY-MM-DD')
    if (!time || time === '') {
      return endDefault ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`
    }
    const [h, m] = time.split(':')
    const sec = endDefault ? '59' : '00'
    return `${dateStr} ${h.padStart(2, '0')}:${(m || '00').padStart(2, '0')}:${sec}`
  }

  const onSearch = (data: OperationLogFiltersInputType) => {
    setValidationError(null)
    const beginStr = buildDateTime(data.beginDate, data.beginTime, false)
    const endStr = buildDateTime(data.endDate, data.endTime, true)

    if (!data.beginDate || !data.endDate) {
      setValidationError(t('Please enter the operation date and time') || '操作日時を入力してください')
      return
    }
    const beginM = moment(beginStr)
    const endM = moment(endStr)
    if (beginM.isAfter(endM)) {
      setValidationError(t('Operation log implementation plan: begin before end') || '開始日時は終了日時より前を指定してください')
      return
    }
    if (endM.diff(beginM, 'months', true) > 3) {
      setValidationError(t('Operation log implementation plan: max 3 months') || '操作日時の期間は3ヶ月以内で指定してください')
      return
    }

    const staffNum = data.staff != null && data.staff !== '' ? Number(data.staff) : undefined
    const reservationIdTrimmed = data.reservationId?.trim()
    const normalizedFeature = normalizeOperationLogFeatureValue(data.featureSlug)
    onChange({
      beginDate: beginStr,
      endDate: endStr,
      ...omit(data, ['beginDate', 'beginTime', 'endDate', 'endTime', 'staff', 'reservationId']),
      staff: staffNum !== undefined && !Number.isNaN(staffNum) ? staffNum : undefined,
      reservationId: reservationIdTrimmed !== '' ? reservationIdTrimmed : undefined,
      featureSlug: normalizedFeature,
      afterClosingAdjustment: Boolean(data.afterClosingAdjustment),
    })
  }

  const fetchHotelEmployeeList = async () => {
    try {
      setEmployeeList(await getHotelEmployeeList())
    } catch (error) {
      errorHandler(error)
    }
  }

  const getLogTranslation = (key: string) => {
    if (i18n.exists(`OperationLog.${key}`)) return t(`OperationLog.${key}`)
    return t(key)
  }

  useEffect(() => {
    fetchHotelEmployeeList()
  }, [])

  return (
    <div css={dropDownResearchPanelStyle}>
      {validationError && <div css={validationErrorStyle}>{validationError}</div>}
      <div css={{ display: 'flex', margin: 24 }}>
        <div css={{ flex: 1 }}>
          <div css={{ margin: 8 }}>
            <Label label={t('Date and time of operation')} />
            <div css={{ display: 'flex', alignItems: 'center', margin: '0 -8px' }}>
              <Controller
                control={control}
                name="beginDate"
                render={({ field: { onChange, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <DateField value={value} onChange={onChange} placeholder={t('Date')} />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="beginTime"
                render={({ field: { onChange, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <Select
                      css={selectBoxStyle}
                      suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                      value={value ?? DEFAULT_BEGIN_TIME}
                      onChange={onChange}
                    >
                      {times.map((item, index) => (
                        <Select.Option key={index} value={item.value}>
                          {item.name === '指定なし' ? '-' : item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
              <div css={{ margin: '0 8px' }}>~</div>
              <Controller
                control={control}
                name="endDate"
                render={({ field: { onChange, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <DateField value={value} onChange={onChange} placeholder={t('Date')} />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="endTime"
                render={({ field: { onChange, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <Select
                      css={selectBoxStyle}
                      suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                      value={value ?? DEFAULT_END_TIME}
                      onChange={onChange}
                    >
                      {times.map((item, index) => (
                        <Select.Option key={index} value={item.value}>
                          {item.name === '指定なし' ? '-' : item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
            </div>
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="staff"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Staff')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={onChange}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {employeeStaff?.map((staff, index) => (
                      <Select.Option key={staff.id ?? `staff-${index}`} value={staff.id}>
                        {staff.name ?? '-'}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
            <Controller
              control={control}
              name="employee"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Account')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={onChange}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {employeeList.map(v => (
                      <Select.Option key={v.id} value={v.id}>
                        {v.name || v.email}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
          </div>
        </div>

        <div css={{ flex: 1 }}>
          <div css={{ display: 'flex', alignItems: 'flex-end' }}>
            <Controller
              control={control}
              name="reservationId"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <InputField value={value ?? ''} handleChangeData={onChange} label={t('Reservation ID')} marginBottom={0} width="100%" />
                </div>
              )}
            />
            <Controller
              control={control}
              name="afterClosingAdjustment"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8, display: 'flex', alignItems: 'center', height: 32 }}>
                  <label css={inlineCheckboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(value)} onChange={e => onChange(e.target.checked)} />
                    <span>{t('OperationLog.afterClosingAdjustment')}</span>
                  </label>
                </div>
              )}
            />
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="featureSlug"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Features')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={onChange}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {FEATURES_LIST.map(v => (
                      <Select.Option key={v.value} value={v.value}>
                        {t(v.i18nKey)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
            <Controller
              control={control}
              name="table"
              render={({ field: { onChange, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Table')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={onChange}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {TABLES_LIST.map(v => (
                      <Select.Option key={v} value={v}>
                        {getLogTranslation(v)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <div css={bottomResearchPanelStyle}>
        <div css={assignButtonContainerStyle}>
          <Button width={92} height={32} buttonType={3} fontSize={12} onClick={onClear}>
            {t('Clear')}
          </Button>
          <Button width={93} height={32} buttonType={1} fontSize={12} marginLeft={16} onClick={handleSubmit(onSearch)}>
            {t('Search')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const dropDownResearchPanelStyle = css({
  width: '100%',
  zIndex: 50,
  backgroundColor: '#FFF',
  position: 'absolute',
  top: 50,
  left: 0,
  boxShadow: '0px 3px 10px #0000003d',
})

const validationErrorStyle = css({
  padding: '12px 24px',
  margin: '0 24px',
  backgroundColor: '#fff3f3',
  color: '#c00',
  fontSize: 12,
  borderRadius: 4,
})

const bottomResearchPanelStyle = css({
  borderTop: '1px solid rgb(242, 242, 242)',
  display: 'flex',
  padding: '16px 32px',
})

const assignButtonContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const selectBoxStyle = css({
  width: 92,
  '> div': {
    borderRadius: '16px',
    border: '1px solid #CCCCCC',
    boxShadow: 'none !important',
    borderColor: '#CCCCCC !important',
  },
})

const inlineCheckboxLabelStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 32,
  fontSize: 13,
  color: '#272727',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
})
